// blackhole/shader.js: GLSL for the Schwarzschild ray tracer (M = 1, G = c = 1).
// Horizon r = 2, photon sphere r = 3, ISCO r = 6, shadow radius b_c = 3*sqrt(3).
//
// Geodesics: in the plane of each ray the orbit obeys d2u/dphi2 = 3u^2 - u
// (u = 1/r). We integrate the equivalent Cartesian system x'' = -3 h^2 x / r^5
// (h = |x × x'|, conserved), whose orbits satisfy exactly that equation, with
// velocity-Verlet and an adaptive step dt = k r. tools/blackhole_physics.py
// checks this integrator against a high-accuracy solution of the orbit
// equation (plane-crossing radii to ~1e-3, escape angles to ~0.02 rad).
import { GLSL_HASH, GLSL_NOISE, GLSL_BLACKBODY } from '../lib/common.js';

export const VERT = /* glsl */`
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

// ---------------------------------------------------------------------------
// One-off disk texture: log-polar noise (u = azimuth / 2pi, v = ln(r/rin) / ln(rout/rin)).
// Sampled on a cylinder so it tiles in azimuth; features are stretched
// along the orbit (radial frequency ELONG x the azimuthal one), which is how
// shear in a Keplerian disk draws turbulence out into streaks.
export const DISKTEX_FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
uniform float uSpan;   // ln(rout / rin)
${GLSL_NOISE}
float ridge(float n) { n = 1.0 - abs(n); return n * n; }
void main() {
  float ang = vUv.x * 6.28318531;
  float v = vUv.y;
  float lr = v * uSpan;                     // ln(r / rin)
  // long arcs sheared along the orbit: ~55 deg long, ~0.25 M wide at r = 10
  const float RA = 1.1;
  vec3 ca = vec3(cos(ang) * RA, sin(ang) * RA, lr * 44.0);
  // large-scale radial wander (~1 M over ~90 deg of azimuth): rings are not
  // perfect circles, so their turning points at the ansae don't stack into chevrons
  vec3 cwn = vec3(cos(ang) * 2.2, sin(ang) * 2.2, lr * 5.0);
  float wz = fbm3(cwn + vec3(3.1, 7.2, 1.9), 3);
  vec3 caw = ca + vec3(0.0, 0.0, 6.0 * wz);
  // R: turbulent emissivity (arcs)
  float arcs = fbm3(caw + vec3(0.0, 0.0, 3.1), 5);
  float em = clamp(0.5 + 0.62 * arcs, 0.0, 1.0);
  // G: ring structure / gaps, mostly radial, slowly varying around the disk
  vec3 cg = vec3(cos(ang) * 0.7, sin(ang) * 0.7, lr * 9.0 + 1.2 * wz);
  float dens = clamp(0.55 + 0.8 * fbm3(cg + vec3(3.3, 9.1, 1.7), 4), 0.0, 1.0);
  // B: thin bright filaments (ridged, thinner and longer than the arcs)
  vec3 cf = vec3(cos(ang) * RA * 1.4, sin(ang) * RA * 1.4, lr * 80.0 + 11.0 * wz);
  float fil = ridge(fbm3(cf + vec3(5.1, 2.2, 8.8), 4));
  // A: large hot clumps along a trailing log-spiral
  float sp = ang + 2.2 * lr;
  vec3 cs = vec3(cos(sp) * 1.6, sin(sp) * 1.6, lr * 3.0);
  float clump = clamp(0.5 + 0.8 * fbm3(cs + vec3(1.9, 4.4, 0.3), 3), 0.0, 1.0);
  gl_FragColor = vec4(em, dens, fil, clump);
}`;

// ---------------------------------------------------------------------------
// One-off sky detail texture (tileable 1:1): R = dust mottling / dark
// filaments, G = unresolved star haze. It is sampled in the Milky Way's own
// coordinates, 16 x 8 tiles over the sphere, so strong lensing stretches real
// fine structure into arcs instead of magnifying the panorama's pixels.
export const SKYDET_FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
${GLSL_NOISE}
float tileN(vec2 uv, float f, vec3 o, int oct) {
  // periodic in u (cylinder) and v (cross-faded with a shifted copy)
  float a = uv.x * 6.28318531;
  float R = f / 6.28318531;
  vec3 p0 = vec3(cos(a) * R, sin(a) * R, uv.y * f) + o;
  vec3 p1 = vec3(cos(a) * R, sin(a) * R, (uv.y - 1.0) * f) + o;
  float w = uv.y;
  float n = fbm3(p0, oct) * (1.0 - w) + fbm3(p1, oct) * w;
  return n * inversesqrt((1.0 - w) * (1.0 - w) + w * w);
}
void main() {
  float d1 = tileN(vUv, 6.0, vec3(1.3, 7.1, 2.2), 6);
  float d2 = tileN(vUv, 22.0, vec3(8.4, 3.3, 5.9), 4);
  float dust = clamp(0.55 + 0.75 * d1 + 0.35 * abs(d2), 0.0, 1.0);
  float h = tileN(vUv, 90.0, vec3(4.2, 1.1, 9.7), 2);
  float haze = smoothstep(0.25, 0.75, h) * (0.4 + 0.6 * smoothstep(0.2, 0.8, dust));
  gl_FragColor = vec4(dust, haze, 0.0, 1.0);
}`;

// ---------------------------------------------------------------------------
// The ray tracer. Defines injected by the JS side: MAX_STEPS, STAR_LAYERS.
export const RAY_FRAG = /* glsl */`
precision highp float;
precision highp sampler2D;
varying vec2 vUv;

uniform vec3 uCamPos;       // black-hole frame, y = disk axis
uniform mat3 uCamBasis;     // columns: right, up, forward
uniform float uTanHalf;     // tan(vertical fov / 2)
uniform float uAspect;      // internal width / height
uniform vec2 uShift;        // subject offset (fractions of the frame, +y up)
uniform float uK, uKin, uHmax, uResc;

uniform sampler2D uDiskTex;
uniform vec2 uDiskTexSize;
uniform float uTime;        // disk time in units of M
uniform float uRin, uRout, uSpan, uFmaxInv;
uniform float uTpeak;       // peak emitted temperature (K), artistic scale
uniform float uBeam;        // intensity ~ g^uBeam (4 = bolometric)
uniform float uDiskGain, uTau0, uIgnite, uFront, uEmber;
uniform float uPixAng;      // radians per internal pixel
uniform float uGcam;        // 1/sqrt(1 - 2/r_cam): blueshift seen by a static camera
uniform vec4 uOrderMask;    // per-image-order opacity (debug; 1 = on)

uniform sampler2D uMW;
uniform sampler2D uSkyDet;
uniform mat3 uSkyRot;
uniform float uMWGain, uStarGain;
uniform float uStarN[4];
uniform float uStarF[4];
uniform float uGain;
uniform float uKnee, uBeamSoft, uHotBlue, uBlueT, uAniso, uSkyDetail, uGrazeSoft;
uniform float uClose, uCloseBlur, uStreak, uRingRes, uRingEps;
uniform vec2 uStreakK;    // close-range streak tiling (azimuth, ln r)
uniform vec2 uCloseR;     // close-range taming: full below .x M of path, off beyond .y
uniform vec4 uGRamp;      // r14 Doppler palette: x = g of lavender, y = g of full blue, z = g of full crimson, w = luma comp
uniform float uPal, uRingTint, uSkyCool;   // palette mix (0 = old blackbody), photon-ring violet, sky cooling

${GLSL_HASH}
${GLSL_BLACKBODY}

const float PI = 3.14159265;
const float TAU = 6.28318531;

// Page & Thorne (1974) flux for a = 0 in closed form (up to a constant).
// Zero torque at the ISCO: F -> 0 at r = 6, peak at r = 9.52.
float pageThorne(float r) {
  float x = sqrt(r);
  const float x0 = 2.44948974, s3 = 1.73205081;
  float lg = log(max(((x - s3) * (x0 + s3)) / ((x + s3) * (x0 - s3)), 1e-6));
  float f = (x - x0 - 0.5 * s3 * lg) / (x * x * x * x * x * (r - 3.0));
  return max(f, 0.0);
}

// Widen the minor axis of a disk-texture footprint (uv gradients) so its
// anisotropy stays <= uAniso: past the sampler's limit the filter under-samples
// the long axis, which showed as moire on the grazing front of the disk.
void clampAniso(inout vec2 gx, inout vec2 gy) {
  vec2 sz = uDiskTexSize.x > 2.0 ? uDiskTexSize : 1.0 / uDiskTexSize;
  vec2 tx = gx * sz, ty = gy * sz;
  float m11 = tx.x * tx.x + ty.x * ty.x, m12 = tx.x * tx.y + ty.x * ty.y, m22 = tx.y * tx.y + ty.y * ty.y;
  float h = 0.5 * (m11 + m22);
  float q = sqrt(max(h * h - (m11 * m22 - m12 * m12), 0.0));
  float l1 = h + q, l2 = h - q;
  if (l1 <= 1e-12 || l2 * uAniso * uAniso >= l1) return;
  vec2 e1 = abs(m12) > 1e-6 * h ? normalize(vec2(m12, l1 - m11)) : (m11 >= m22 ? vec2(1.0, 0.0) : vec2(0.0, 1.0));
  float a1 = sqrt(l1);
  gx = e1 * a1 / sz;
  gy = vec2(-e1.y, e1.x) * (a1 / uAniso) / sz;
}

// Same cap for the close-range streak layer (sz = texel scale of its coordinates).
void capAniso(inout vec2 gx, inout vec2 gy, vec2 sz) {
  vec2 tx = gx * sz, ty = gy * sz;
  float m11 = tx.x * tx.x + ty.x * ty.x, m12 = tx.x * tx.y + ty.x * ty.y, m22 = tx.y * tx.y + ty.y * ty.y;
  float h = 0.5 * (m11 + m22);
  float q = sqrt(max(h * h - (m11 * m22 - m12 * m12), 0.0));
  float l1 = h + q, l2 = h - q;
  if (l1 <= 1e-12 || l2 * uAniso * uAniso >= l1) return;
  vec2 e1 = abs(m12) > 1e-6 * h ? normalize(vec2(m12, l1 - m11)) : (m11 >= m22 ? vec2(1.0, 0.0) : vec2(0.0, 1.0));
  float a1 = sqrt(l1);
  gx = e1 * a1 / sz;
  gy = vec2(-e1.y, e1.x) * (a1 / uAniso) / sz;
}

float gStreak = 1.0;   // close-range streak multiplier (mean ~1), written by diskNoise

// Keplerian flow noise: two phases cross-faded over a period P so the shear
// never winds the pattern into sub-pixel stripes (stateless in time).
// fx, fy: the pixel footprint on the disk along screen x / y, as
// (d phi [rad], d r [M]); turned into texture gradients per phase, including
// the shear term (u depends on r through Omega(r)), so the anisotropic filter
// sees the true footprint.
vec4 diskNoise(float r, float phi, vec2 fx, vec2 fy, float cl) {
  float om = pow(r, -1.5);
  float v = log(r / uRin) / uSpan;
  const float P = 45.0;
  float ph = uTime / P;
  float fA = fract(ph), fB = fract(ph + 0.5);
  float cA = floor(ph), cB = floor(ph + 0.5);
  // winding is centred on each phase's peak weight: the dominant phase is
  // always nearly unsheared (no chevrons), the fading one wound by +-P/2
  float sA = fA - 0.5, sB = fB - 0.5;
  float uA = (phi - om * sA * P) / TAU + hash11(cA * 7.13 + 1.0);
  float uB = (phi - om * sB * P) / TAU + hash11(cB * 7.13 + 4.0);
  float sh = 1.5 * om / r * P;                 // -dOmega/dr * P
  float iv = 1.0 / (r * uSpan);
  vec2 gAx = vec2((fx.x + sh * sA * fx.y) / TAU, fx.y * iv), gAy = vec2((fy.x + sh * sA * fy.y) / TAU, fy.y * iv);
  vec2 gBx = vec2((fx.x + sh * sB * fx.y) / TAU, fx.y * iv), gBy = vec2((fy.x + sh * sB * fy.y) / TAU, fy.y * iv);
  clampAniso(gAx, gAy);
  clampAniso(gBx, gBy);
  vec4 a = textureGrad(uDiskTex, vec2(uA, v), gAx, gAy);
  vec4 b = textureGrad(uDiskTex, vec2(uB, v), gBx, gBy);
  float wA = 1.0 - abs(2.0 * fA - 1.0);
  float wB = 1.0 - wA;
  vec4 m = a * wA + b * wB;
  float nrm = inversesqrt(wA * wA + wB * wB);
  gStreak = 1.0;
  if (cl > 0.0) {
    // close range: fine turbulent streaks drawn out along the orbit (~18:1, the
    // tileable detail texture), flowing with the same two phases; mean-preserving
    vec2 KS = uStreakK;
    vec2 sz = vec2(textureSize(uSkyDet, 0));
    // shear u by v so the tile's radial period doesn't line up into a rhythm
    const float SH = 0.37;
    vec2 hAx = vec2(KS.x * gAx.x + SH * KS.y * gAx.y, KS.y * gAx.y), hAy = vec2(KS.x * gAy.x + SH * KS.y * gAy.y, KS.y * gAy.y);
    vec2 hBx = vec2(KS.x * gBx.x + SH * KS.y * gBx.y, KS.y * gBx.y), hBy = vec2(KS.x * gBy.x + SH * KS.y * gBy.y, KS.y * gBy.y);
    capAniso(hAx, hAy, sz);
    capAniso(hBx, hBy, sz);
    float sa = textureGrad(uSkyDet, vec2(uA * KS.x + SH * KS.y * v, v * KS.y), hAx, hAy).r;
    float sb = textureGrad(uSkyDet, vec2(uB * KS.x + SH * KS.y * v, v * KS.y) + 0.5, hBx, hBy).r;
    float smu = textureLod(uSkyDet, vec2(0.5), 12.0).r;
    gStreak = max(1.0 + cl * uStreak * (sa * wA + sb * wB - smu) * nrm / max(smu, 0.05), 0.0);
  }
  return clamp((m - 0.5) * nrm + 0.5, 0.0, 1.0);
}

// Shade one disk crossing. Runs after the march so screen derivatives are
// valid: h = (x, z, v_y, path length), dPx / dPy = screen derivatives of the
// hit point (x, z), i.e. the true (lensed) pixel footprint on the disk.
// Doppler palette (r14, LIGHT.md GRAVITY). The hue is a monotonic function of the
// real redshift factor g: the direction is physical, the hues are artistic.
// Lavender-white #CDBBFF at g = uGRamp.x; ice-blue #CFEAFF / blue #5AA9FF as g
// rises to uGRamp.y (approaching); magenta #FF3D7F -> crimson #7A0F3A as g falls
// to uGRamp.z (receding; the crimson's darkness is the g^beam dimming). Higher
// image orders (the photon ring) pull toward violet-white #EDE6FF. Every stop has
// B >= G, so no per-channel tone-map shoulder can push it into gold hues.
vec3 dopplerRamp(float g, float F, float hot, float n) {
  float lg = log(max(g, 1e-3) / uGRamp.x);
  float wb = pow(clamp(lg / log(uGRamp.y / uGRamp.x), 0.0, 1.0), 0.8);
  float wr = pow(clamp(lg / log(uGRamp.z / uGRamp.x), 0.0, 1.0), 1.2);
  vec3 c = mix(vec3(0.58, 0.46, 1.0), vec3(0.12, 0.37, 1.0), wb);   // #5AA9FF, G trimmed: ACES drifts bright blue to cyan
  c = mix(c, vec3(1.0, 0.20, 0.50), smoothstep(0.0, 0.55, wr));      // magenta #FF3D7F (pre-tone-map)
  c = mix(c, vec3(1.0, 0.07, 0.34), smoothstep(0.55, 1.0, wr));      // crimson #7A0F3A (dark = g^beam)
  c = mix(c, vec3(0.62, 0.82, 1.0), hot * uHotBlue);
  c = mix(c, vec3(0.85, 0.79, 1.0), 0.2 * smoothstep(0.45, 1.0, F) * (1.0 - 0.8 * wr));
  // photon ring (orders >= 2) violet-white; the first lensed image (the arc over
  // the shadow) only a touch, so its Doppler asymmetry still reads
  c = mix(c, vec3(0.85, 0.79, 1.0), uRingTint * step(0.5, n) * (n > 1.5 ? 1.0 : 0.3));
  float L = dot(c, vec3(0.2126, 0.7152, 0.0722));
  return c * mix(1.0, 0.72 / max(L, 0.05), uGRamp.w);
}

void shadeHit(vec4 h, vec2 dPx, vec2 dPy, vec2 bad, float by, float n, inout vec3 col, inout float T) {
  float r = length(h.xy);
  if (r < 2.0) return;
  vec2 er = h.xy / r;
  vec2 ephi = vec2(-er.y, er.x);
  float cosi = abs(h.z);
  // grazing view: the nonlinear lane/gap shaping re-sharpens filtered noise
  // past Nyquist (moire on the front of the disk); soften it and bias the LOD
  float grz = (1.0 - smoothstep(0.03, 0.2, cosi)) * uGrazeSoft;
  // analytic footprint (straight-line estimate, demagnified ~e^pi per half orbit)
  float foot0 = uPixAng * h.w / max(cosi, 0.03) * exp(n * 3.1);
  if (bad.x > 0.5) dPx = bad.y > 0.5 ? er * foot0 : dPy;
  if (bad.y > 0.5) dPy = bad.x > 0.5 ? ephi * foot0 : dPx;
  float edge = 1.0 - 0.25 * (bad.x + bad.y);
  float cap = 4.0 * foot0;
  float lx = length(dPx), ly = length(dPy);
  if (lx > cap) dPx *= cap / lx;
  if (ly > cap) dPy *= cap / ly;
  vec2 fx = vec2(dot(dPx, ephi) / r, dot(dPx, er));
  vec2 fy = vec2(dot(dPy, ephi) / r, dot(dPy, er));
  float w = abs(fx.y) + abs(fy.y) + 1e-4;
  // higher-order images are thinner than a pixel: never trust a footprint
  // narrower than a fraction of the analytic one
  if (n > 1.5) w = max(w, 0.5 * foot0);
  float cov = clamp((min(r + 0.5 * w, uRout) - max(r - 0.5 * w, uRin - 0.3)) / w, 0.0, 1.0);
  if (cov <= 0.0) return;
  float phi = atan(-h.y, h.x);
  float rs = clamp(r, uRin, uRout);
  // close range (the dive): seen nearly face-on from a few M, the noise's large
  // shapes read as big marbled swirls. Bias the LOD and fade the texture contrast
  // toward its mean with distance (artistic). Exactly off beyond uCloseR.y of path,
  // so the hero (path >= ~40 M) is bit-identical.
  float cl = uClose * (1.0 - smoothstep(uCloseR.x, uCloseR.y, h.w));
  float fk = 1.0 + 0.35 * grz + uCloseBlur * cl;
  vec4 nz = diskNoise(rs, phi, fx * fk, fy * fk, cl);
  if (cl > 0.0) {
    vec4 mu = textureLod(uDiskTex, vec2(0.5), 12.0);   // top mip = texture mean
    nz = mu + (nz - mu) * (1.0 - cl);
  }

  float inner = smoothstep(uRin - 0.3, uRin + 1.2, rs);
  float outer = 1.0 - smoothstep(uRout * 0.45, uRout, rs);
  // density: lanes and gaps you can see through to the images behind
  float lane = smoothstep(0.2 - 0.15 * grz, 0.7 + 0.15 * grz, nz.g);
  float dens = inner * outer * ((0.05 + 1.7 * lane * lane) * (0.55 + 0.8 * nz.a) + 0.4 * nz.b * nz.b);
  float tau = uTau0 * dens / max(cosi, 0.04);
  float alpha = (1.0 - exp(-tau)) * cov * edge * uOrderMask[int(n + 0.5)];
  r = rs;

  float F = pageThorne(max(r, uRin + 1e-3)) * uFmaxInv;          // 0..1
  float om = pow(r, -1.5);
  // redshift of the orbiting gas as seen by the (static) camera:
  // g = sqrt(1 - 3/r) / (1 - Omega b_y) / sqrt(1 - 2/r_cam)
  float g = sqrt(max(1.0 - 3.0 / r, 0.0)) / max(1.0 - om * by, 0.05) * uGcam;
  // source function: hot sheared arcs + bright thin filaments
  float em = nz.r * nz.r;
  // Beaming g^uBeam in full on the receding side. On the approaching side the
  // boost and the blueshift of the colour are toned (artistic): it stays the
  // brightest part of the frame but keeps its structure (r14: ice-blue, see dopplerRamp)
  // through the tone map; only the hottest inner filaments turn blue-white.
  float gB = pow(g, uBeam);
  if (gB > 1.0) gB = 1.0 + (gB - 1.0) / (1.0 + (gB - 1.0) * uBeamSoft);
  float gT = g > 1.0 ? pow(g, uBlueT) : g;
  float hot = smoothstep(1.05, 1.3, g) * smoothstep(0.4, 0.85, F) * smoothstep(0.25, 0.8, em);
  float Tobs = mix(uTpeak * pow(F, 0.25) * gT, 25000.0, hot * uHotBlue);
  float I = F * gB * (0.1 + 2.6 * em) * (0.6 + 1.0 * nz.b * nz.b) * (0.7 + 0.6 * nz.a);
  I *= gStreak;
  float lit = uEmber + uIgnite * smoothstep(uFront + 3.0, uFront - 1.0, r);
  // unlit gas is thin (artistic reveal): the arrival shows the bare shadow
  alpha *= clamp(lit * 1.25, 0.0, 1.0);
  vec3 dc = dopplerRamp(g, F, hot, n);
  if (uPal < 1.0) dc = mix(blackbody(max(Tobs, 1000.0)), dc, uPal);   // pal 0 = the r13 blackbody look (A/B)
  vec3 c = dc * I * uDiskGain * lit;
  // hue-preserving log shoulder per crossing: above the knee every doubling adds
  // a constant step, so the multiplicative texture survives the ACES shoulder
  float Lc = max(c.r, max(c.g, c.b));
  if (Lc > uKnee) c *= uKnee * (1.0 + log(Lc / uKnee)) / Lc;
  col += T * alpha * c;
  T *= 1.0 - alpha;
}

// Equi-angular cube face coordinates of a unit vector: (face, u, v), u, v in [0, 1)
vec3 cubeFace(vec3 d, out vec3 fu, out vec3 fv, out vec3 fn) {
  vec3 a = abs(d);
  float face;
  vec2 uv;
  if (a.x >= a.y && a.x >= a.z) {
    face = d.x > 0.0 ? 0.0 : 1.0;
    fn = vec3(sign(d.x), 0.0, 0.0); fu = vec3(0.0, 0.0, 1.0); fv = vec3(0.0, 1.0, 0.0);
    uv = d.zy / a.x;
  } else if (a.y >= a.z) {
    face = d.y > 0.0 ? 2.0 : 3.0;
    fn = vec3(0.0, sign(d.y), 0.0); fu = vec3(1.0, 0.0, 0.0); fv = vec3(0.0, 0.0, 1.0);
    uv = d.xz / a.y;
  } else {
    face = d.z > 0.0 ? 4.0 : 5.0;
    fn = vec3(0.0, 0.0, sign(d.z)); fu = vec3(1.0, 0.0, 0.0); fv = vec3(0.0, 1.0, 0.0);
    uv = d.xy / a.z;
  }
  uv = atan(uv) * (4.0 / PI);              // equi-angular warp, -1..1
  return vec3(face, uv * 0.5 + 0.5);
}

float gLz = 0.0;   // Einstein-ring zone weight of the current sky sample (set in skyColor)

// Analytic point stars, filtered through the lensing Jacobian: dx, dy are the
// screen derivatives of the (lensed) sky direction, so a star is drawn as a
// ~1-pixel spot at its true image position, stretched into an arc where the
// map is strongly magnified. Brightness scales with magnification.
vec3 stars(vec3 d, vec3 dx, vec3 dy, float bad, float glow) {
  vec3 fu, fv, fn;
  vec3 fuv = cubeFace(d, fu, fv, fn);
  float a11 = dot(dx, dx), a12 = dot(dx, dy), a22 = dot(dy, dy);
  const float S2 = 0.36;                      // pixel kernel sigma^2 (px^2)
  float eps = 2.5e-9 / S2 * mix(1.0, uRingEps, gLz);   // wider in the ring zone on coarse tiers: longer arcs that join into a ring                    // intrinsic star size (rad^2 / px^2)
  float m11 = a11 + eps, m22 = a22 + eps, det = max(m11 * m22 - a12 * a12, 1e-24);
  float srcA = sqrt(det);                     // source solid angle per pixel (regularised)
  // major axis of the pixel footprint on the sky: near the critical curve the
  // map is hugely anisotropic, and a pixel can span many star cells radially
  float tr = 0.5 * (m11 + m22);
  float lmax = tr + sqrt(max(tr * tr - det, 0.0));
  float pixA = uPixAng * uPixAng;
  float mag = min(pixA / srcA, 60.0);
  // Einstein-ring zone (gLz): relax the resolve limits so the stretched star
  // layers stay crisp arcs instead of fading into the mean haze
  float lw = gLz;
  vec3 acc = vec3(0.0);
  for (int k = 0; k < STAR_RING; k++) {
    // layers past the tier's budget run only in the Einstein-ring zone (arrival),
    // so the cheap tiers still resolve the ring into thin continuous star arcs
    float wl = k < STAR_LAYERS ? 1.0 : smoothstep(0.02, 0.4, lw);
    if (wl <= 0.0) break;
    float N = uStarN[k];
    vec2 g = fuv.yz * N;
    vec2 cell = floor(g);
    vec3 h = hash33(vec3(cell, fuv.x * 17.0 + float(k) * 131.0));
    // star position inside the cell (margin keeps the kernel inside it)
    vec2 sp = (cell + 0.22 + 0.56 * h.xy) / N * 2.0 - 1.0;
    sp = tan(sp * (PI / 4.0));
    vec3 s = normalize(fn + fu * sp.x + fv * sp.y);
    vec3 D = s - d;
    float j1 = dot(dx, D), j2 = dot(dy, D);
    vec2 del = vec2(m22 * j1 - a12 * j2, m11 * j2 - a12 * j1) / det;   // pixel offset to the image
    float cellAng = (PI / 2.0) / N;
    float fade = smoothstep(0.35, 0.8, mix(1.0, 0.4, lw) * 3.0 * sqrt(srcA) / (0.22 * cellAng));
    fade = max(fade, smoothstep(mix(0.12, 0.24, lw), mix(0.3, uRingRes, lw), sqrt(lmax) / cellAng));
    fade = max(fade, bad);
    float Fk = uStarF[k] * exp2(-7.0 * h.z * h.z) * mix(1.0, glow, float(k) / 3.0);
    float lum = step(0.12, h.z * 0.9 + 0.1 * float(k));
    // r14: cooler star colours with uSkyCool (no orange K/M giants dominating the arcs)
    float temp = mix(mix(3200.0, 6200.0, uSkyCool), mix(12500.0, 18000.0, uSkyCool), pow(fract(h.z * 91.7 + h.x * 13.1), mix(2.2, 1.2, uSkyCool)));
    vec3 tint = blackbody(temp * uGcam);
    float spot = exp(-dot(del, del) / (2.0 * S2)) / (2.0 * PI * S2) * mag;
    // unresolved: the mean over all cells (not this cell's star), so the
    // fallback is smooth instead of a mosaic of per-cell blocks
    float avg = uStarF[k] * 0.36 * mix(1.0, glow, float(k) / 3.0) / (cellAng * cellAng) * pixA;
    acc += wl * (tint * Fk * lum * spot * (1.0 - fade) * (1.0 + 2.0 * lw) + vec3(0.95, 0.93, 1.0) * avg * fade);
  }
  return acc;
}

vec3 skyColor(vec3 d, vec3 dx, vec3 dy, float bad) {
  vec3 g = uSkyRot * d, gx = uSkyRot * dx, gy = uSkyRot * dy;
  float rxy2 = max(g.x * g.x + g.y * g.y, 1e-8);
  float l = atan(g.y, g.x);
  float b = asin(clamp(g.z, -1.0, 1.0));
  vec2 uv = vec2(0.5 - l / TAU, 0.5 + b / PI);
  float ir = inversesqrt(rxy2);
  vec2 ddx = vec2(-(g.x * gx.y - g.y * gx.x) / rxy2 / TAU, gx.z * ir / PI);
  vec2 ddy = vec2(-(g.x * gy.y - g.y * gy.x) / rxy2 / TAU, gy.z * ir / PI);
  vec3 mw = textureGrad(uMW, uv, ddx, ddy).rgb;
  // Einstein-ring zone: where lensing stretches the sky (source footprint axis
  // ratio > ~1.5) the panorama (~0.09 deg/texel) smears into soft arcs. There the
  // analytic stars lead: the glow is unsharp-masked along the footprint and
  // dimmed a little, and the star layers are resolved further and boosted.
  float a11 = dot(dx, dx), a12 = dot(dx, dy), a22 = dot(dy, dy);
  float th = 0.5 * (a11 + a22), qd = sqrt(max(th * th - (a11 * a22 - a12 * a12), 0.0));
  float ar = sqrt((th + qd) / max(th - qd, 1e-3 * (th + qd) + 1e-30));
  float lz = smoothstep(1.3, 3.0, ar) * uSkyDetail * (1.0 - bad);
  gLz = lz;
  if (lz > 0.0) {
    vec3 mwB = textureGrad(uMW, uv, ddx * 2.5, ddy * 2.5).rgb;
    mw = max(mw + 1.4 * lz * (mw - mwB), 0.0);
    // fine dust filaments 4x finer than the detail layer: lensing stretches them
    // into long arcs that stay sharp across the ring (anisotropic textureGrad)
#if SKY_FIL
    vec2 det2 = textureGrad(uSkyDet, uv * vec2(64.0, 32.0) + 0.37, ddx * vec2(64.0, 32.0), ddy * vec2(64.0, 32.0)).rg;
    mw *= mix(1.0, 0.3 + 1.1 * det2.r, lz);
#endif
  }
  // fine dust + star haze below the panorama's resolution (mean ~1)
  const vec2 KD = vec2(16.0, 8.0);
  vec2 det = textureGrad(uSkyDet, uv * KD, ddx * KD, ddy * KD).rg;
  float glow = clamp(dot(mw, vec3(0.3, 0.5, 0.2)) * 6.0, 0.0, 1.0);
  mw *= 0.3 + 1.25 * det.r + 0.9 * det.g * glow;
  mw *= 1.0 - 0.4 * gLz;
  // r14 cool blue-violet sky (artistic): keep the panorama's luminance, map its
  // warm/cool axis onto violet (dust, bulge) and blue (star clouds)
  if (uSkyCool > 0.0) {
    float Ls = dot(mw, vec3(0.2126, 0.7152, 0.0722));
    float wm = smoothstep(-0.1, 0.5, (mw.r - mw.b) / max(Ls, 1e-4));
    mw = mix(mw, Ls * mix(vec3(0.78, 0.98, 1.72), vec3(1.2, 0.76, 2.1), wm), uSkyCool);
  }
  vec3 c = mw * uMWGain;
  c += stars(d, dx, dy, bad, 0.4 + 2.5 * glow) * uStarGain;
  // blueshift / beaming of the sky seen by a static observer deep in the well
  float gb = uGcam;
  c *= gb * gb * gb;
  c *= mix(vec3(1.0), vec3(0.75, 0.9, 1.35), clamp((gb - 1.0) * 0.6, 0.0, 1.0));
  return c;
}

void main() {
  vec2 ndc = (vUv - 0.5 - uShift) * 2.0;
  vec3 dirCam = normalize(vec3(ndc.x * uTanHalf * uAspect, ndc.y * uTanHalf, 1.0));
  vec3 d = uCamBasis * dirCam;

  vec3 p = uCamPos;
  float rc = length(p);
  vec3 rh = p / rc;
  float dr = dot(d, rh);
  // static observer's local direction -> coordinate velocity (tan psi = tan chi / sqrt(1-2/r))
  vec3 v = d + rh * dr * (sqrt(max(1.0 - 2.0 / rc, 1e-6)) - 1.0);
  // the orbit equation is invariant under v -> lambda v (t -> t / lambda):
  // normalise so rays launched near the horizon don't crawl in tiny steps
  v = normalize(v);
  vec3 hv = cross(p, v);
  float h2 = dot(hv, hv);
  float vinf = sqrt(max(dot(v, v) - 2.0 * h2 / (rc * rc * rc), 1e-10));
  float by = -hv.y / vinf;            // photon angular momentum about the disk axis, per unit energy

  vec3 col = vec3(0.0);
  float T = 1.0;
  float r = rc;
  vec3 a = -3.0 * h2 * p / (r * r * r * r * r);
  float path = 0.0;
  float nh = 0.0;                     // disk crossings recorded
  vec4 H0 = vec4(0.0), H1 = vec4(0.0), H2 = vec4(0.0), H3 = vec4(0.0);
  float fate = 0.0;                   // 0 running/steps, 1 escaped, 2 captured, 3 hit budget
  float vl = length(v);
  for (int i = 0; i < MAX_STEPS; i++) {
    // continuous step size: a hard switch at some radius makes the discrete
    // orbit jump between neighbouring pixels, and near the photon sphere that
    // jump is amplified ~e^pi per half orbit (beads along the photon ring)
    float dt = min(uHmax, mix(uKin, uK, smoothstep(3.2, 6.5, r)) * r);
    vec3 pn = p + v * dt + 0.5 * a * (dt * dt);
    float rn = length(pn);
    float rn2 = rn * rn;
    vec3 an = -3.0 * h2 * pn / (rn2 * rn2 * rn);
    vec3 vn = v + 0.5 * (a + an) * dt;
    if (pn.y * p.y < 0.0) {
      // plane crossing: root of the cubic Hermite through (p, v dt, pn, vn dt).
      // Linear interpolation jumps whenever the bracketing step changes between
      // neighbouring pixels, and lensing amplifies that into beads on the rings.
      vec3 m0 = v * dt, m1 = vn * dt;
      float s = p.y / (p.y - pn.y);
      for (int it = 0; it < 2; it++) {
        float s2 = s * s, s3 = s2 * s;
        float y = (2.0 * s3 - 3.0 * s2 + 1.0) * p.y + (s3 - 2.0 * s2 + s) * m0.y + (3.0 * s2 - 2.0 * s3) * pn.y + (s3 - s2) * m1.y;
        float dy = (6.0 * s2 - 6.0 * s) * p.y + (3.0 * s2 - 4.0 * s + 1.0) * m0.y + (6.0 * s - 6.0 * s2) * pn.y + (3.0 * s2 - 2.0 * s) * m1.y;
        s = clamp(s - y / (abs(dy) > 1e-7 ? dy : 1e-7), 0.0, 1.0);
      }
      float s2 = s * s, s3 = s2 * s;
      vec3 pc = (2.0 * s3 - 3.0 * s2 + 1.0) * p + (s3 - 2.0 * s2 + s) * m0 + (3.0 * s2 - 2.0 * s3) * pn + (s3 - s2) * m1;
      vec3 vc = normalize((6.0 * s2 - 6.0 * s) * p + (3.0 * s2 - 4.0 * s + 1.0) * m0 + (6.0 * s - 6.0 * s2) * pn + (3.0 * s2 - 2.0 * s) * m1);
      // record every crossing (gap and outskirts too) so that the n-th crossing
      // is the same image in neighbouring pixels and its derivatives are valid
      vec4 hh = vec4(pc.x, pc.z, vc.y, path + s * dt * vl);
      if (nh < 0.5) H0 = hh; else if (nh < 1.5) H1 = hh; else if (nh < 2.5) H2 = hh; else H3 = hh;
      nh += 1.0;
    }
    path += dt * vl;
    p = pn; v = vn; a = an; r = rn;
    vl = length(v);
    if (r < 2.0) { fate = 2.0; break; }
    if (r > uResc && dot(p, v) > 0.0) { fate = 1.0; break; }
    if (nh > float(MAX_HITS) - 0.5) { fate = 3.0; break; }
  }

  // shade the crossings front to back, with the lensed pixel footprint on the
  // disk from screen derivatives (uniform control flow: take them all first).
  // A quad neighbour without an n-th crossing gives a junk derivative: detect
  // it, fall back to the other axis, and treat the pixel as half covered.
  vec2 d0x = dFdx(H0.xy), d0y = dFdy(H0.xy);
  vec2 d1x = dFdx(H1.xy), d1y = dFdy(H1.xy);
  vec2 d2x = dFdx(H2.xy), d2y = dFdy(H2.xy);
  vec2 d3x = dFdx(H3.xy), d3y = dFdy(H3.xy);
  vec4 vf = step(vec4(0.5, 1.5, 2.5, 3.5), vec4(nh));
  vec4 bx = abs(dFdx(vf)), by4 = abs(dFdy(vf));
  if (nh > 0.5) shadeHit(H0, d0x, d0y, vec2(bx.x, by4.x), by, 0.0, col, T);
  if (nh > 1.5) shadeHit(H1, d1x, d1y, vec2(bx.y, by4.y), by, 1.0, col, T);
  if (nh > 2.5) shadeHit(H2, d2x, d2y, vec2(bx.z, by4.z), by, 2.0, col, T);
  if (nh > 3.5) shadeHit(H3, d3x, d3y, vec2(bx.w, by4.w), by, 3.0, col, T);

  // weak-field tail beyond uResc: remaining bend (M / b)(2 - 3 mu + mu^3) toward the hole
  vec3 vv = v / max(vl, 1e-8);
  float mu = dot(p, vv) / r;
  float bl = sqrt(h2) / max(vl, 1e-8);
  float extra = (2.0 - 3.0 * mu + mu * mu * mu) / max(bl, 1e-3);
  vec3 cperp = p - dot(p, vv) * vv;
  float cl = length(cperp);
  vec3 dirEsc = cl > 1e-6 ? normalize(vv * cos(extra) - (cperp / cl) * sin(extra)) : vv;

  float esc = fate == 1.0 ? 1.0 : 0.0;
  vec3 ddx = dFdx(dirEsc), ddy = dFdy(dirEsc);
  float bad = clamp(fwidth(esc) * 4.0, 0.0, 1.0);
  if (esc > 0.5) col += T * skyColor(dirEsc, ddx, ddy, bad);
  col = max(col, vec3(0.0)) * uGain;
  // highlight knee above 1 (hue-preserving): the Doppler-boosted side keeps its
  // texture instead of clipping to a flat white blob after the tone map
  float Lm = max(col.r, max(col.g, col.b));
  if (Lm > 1.0) col *= (1.0 + (Lm - 1.0) / (1.0 + (Lm - 1.0) * 0.45)) / Lm;
  gl_FragColor = vec4(col, 1.0);
}`;
